<template>
  <div class="container py-5">
    <div class="row">
      <div class="col-12">
        <div class="page-header">
          <h1 class="page-title">Offres d'emploi</h1>
          <p class="page-subtitle">Découvrez toutes nos offres d'emploi disponibles</p>
        </div>

        <!-- Filtres -->
        <div class="filters-section mb-4">
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label for="statusFilter">Statut</label>
                <select v-model="filters.status" class="form-control" id="statusFilter">
                  <option value="">Tous les statuts</option>
                  <option value="active">Offres actives</option>
                  <option value="expired">Offres expirées</option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label for="searchFilter">Recherche</label>
                <input 
                  type="text" 
                  v-model="filters.search" 
                  class="form-control" 
                  id="searchFilter"
                  placeholder="Rechercher par titre..."
                >
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label>&nbsp;</label>
                <div class="d-flex gap-2">
                  <button @click="clearFilters" class="btn btn-outline-secondary flex-fill">
                    <i class="fas fa-times"></i> Réinitialiser
                  </button>
                  <button @click="refreshCache" class="btn btn-outline-primary" title="Rafraîchir les données">
                    <i class="fas fa-sync-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Liste des offres -->
        <LoadingSpinner v-if="loading" text="Chargement des offres d'emploi..." />

        <template v-else>
          <!-- Offres actives -->
          <div v-if="filters.status !== 'expired'" class="offers-section">
            <div class="section-header d-flex align-items-center justify-content-between mb-3">
              <h3 class="section-subtitle">
                <i class="fas fa-bolt text-success"></i>
                Offres en cours
                <span class="count-badge">{{ activeOffers.length }}</span>
              </h3>
            </div>

            <div v-if="activeOffers.length === 0" class="no-offers text-center">
              <i class="fas fa-briefcase fa-3x text-muted mb-3"></i>
              <h4>Aucune offre active pour le moment</h4>
              <p class="text-muted">Revenez bientot pour decouvrir nos nouvelles opportunites.<br>
                Vous pouvez aussi consulter nos offres passees ci-dessous.
              </p>
            </div>

            <div v-else class="job-offers-grid">
              <div v-for="offer in activeOffers" :key="'a-' + offer.id" class="job-offer-item">
                <div class="card h-100">
                  <div class="card-header d-flex justify-content-between align-items-start">
                    <h5 class="card-title mb-0">{{ offer.title }}</h5>
                    <div class="status-badges">
                      <span class="badge badge-success">En cours</span>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="offer-content" v-html="offer.description"></div>
                    <div class="offer-details mt-3">
                      <div class="detail-row">
                        <i class="fas fa-calendar-alt text-primary"></i>
                        <span><strong>Date limite:</strong> {{ formatDate(offer.deadline) }}</span>
                      </div>
                      <div class="detail-row">
                        <i class="fas fa-clock text-primary"></i>
                        <span><strong>Jours restants:</strong> {{ getRemainingDays(offer.deadline) }} jour(s)</span>
                      </div>
                      <div v-if="offer.pdf_file" class="detail-row">
                        <i class="fas fa-file-pdf text-danger"></i>
                        <button @click="downloadPdf(offer)" class="btn btn-link text-decoration-none p-0">
                          <strong>Telecharger le PDF</strong>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="card-footer">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="action-buttons">
                        <button class="btn btn-primary btn-sm" @click="viewDetails(offer.id)">
                          <i class="fas fa-eye"></i> Voir details
                        </button>
                        <button class="btn btn-warning btn-sm" @click="applyNow(offer.id)">
                          <i class="fas fa-paper-plane"></i> Postuler
                        </button>
                      </div>
                      <div class="share-buttons">
                        <button class="btn btn-outline-primary btn-sm" @click="shareOnWhatsApp(offer)">
                          <i class="fab fa-whatsapp"></i>
                        </button>
                        <button class="btn btn-outline-primary btn-sm" @click="shareOnFacebook(offer)">
                          <i class="fab fa-facebook"></i>
                        </button>
                        <button class="btn btn-outline-primary btn-sm" @click="shareOnLinkedIn(offer)">
                          <i class="fab fa-linkedin"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Offres passees -->
          <div v-if="filters.status !== 'active' && pastOffers.length > 0" class="offers-section mt-5">
            <div class="section-header past-header">
              <div>
                <h3 class="section-subtitle">
                  <i class="fas fa-history text-secondary"></i>
                  Offres passees
                  <span class="count-badge count-muted">{{ pastOffers.length }}</span>
                </h3>
                <p class="text-muted mb-0 small">
                  Un apercu de notre historique de recrutements pour temoigner de notre activite.
                </p>
              </div>
              <button v-if="pastOffers.length > 3" @click="showAllPast = !showAllPast" class="btn btn-sm btn-outline-secondary">
                <i :class="showAllPast ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                {{ showAllPast ? 'Masquer' : `Voir toutes (${pastOffers.length})` }}
              </button>
            </div>

            <div class="job-offers-grid past-grid mt-3">
              <div v-for="offer in visiblePastOffers" :key="'p-' + offer.id" class="job-offer-item past-offer">
                <div class="card h-100">
                  <div class="card-header d-flex justify-content-between align-items-start">
                    <h5 class="card-title mb-0">{{ offer.title }}</h5>
                    <span class="badge badge-secondary">Cloturee</span>
                  </div>
                  <div class="card-body">
                    <div class="offer-content" v-html="offer.description"></div>
                    <div class="offer-details mt-3">
                      <div class="detail-row">
                        <i class="fas fa-calendar-times text-muted"></i>
                        <span><strong>Cloturee le:</strong> {{ formatDate(offer.deadline) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="card-footer">
                    <button class="btn btn-outline-secondary btn-sm" @click="viewDetails(offer.id)">
                      <i class="fas fa-eye"></i> Consulter l'offre
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Message aucune offre du tout -->
          <div v-if="activeOffers.length === 0 && pastOffers.length === 0" class="text-center">
            <div class="no-offers">
              <i class="fas fa-briefcase fa-3x text-muted mb-3"></i>
              <h4>Aucune offre trouvee</h4>
              <p class="text-muted">Aucune offre ne correspond a vos criteres de recherche.</p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '../components/LoadingSpinner.vue'

export default {
  name: 'JobOffers',
  components: {
    LoadingSpinner
  },
  
  data() {
    return {
      jobOffers: [],
      loading: true,
      filters: {
        status: '',
        search: ''
      },
      showAllPast: false
    }
  },

  computed: {
    searchedOffers() {
      if (!this.filters.search) return this.jobOffers;
      const search = this.filters.search.toLowerCase();
      return this.jobOffers.filter(offer =>
        (offer.title || '').toLowerCase().includes(search) ||
        (offer.description || '').toLowerCase().includes(search)
      );
    },
    activeOffers() {
      return this.searchedOffers.filter(
        offer => offer.is_active && !this.isExpired(offer.deadline)
      );
    },
    pastOffers() {
      return this.searchedOffers
        .filter(offer => !offer.is_active || this.isExpired(offer.deadline))
        .sort((a, b) => new Date(b.deadline) - new Date(a.deadline));
    },
    visiblePastOffers() {
      return this.showAllPast ? this.pastOffers : this.pastOffers.slice(0, 3);
    }
  },
  
  async mounted() {
    // Charger les offres avec un délai minimal pour éviter le flash
    this.loading = true
    setTimeout(async () => {
      await this.loadJobOffers()
    }, 100)
  },
  
  methods: {
    async loadJobOffers() {
      try {
        // Utiliser un cache local pour améliorer les performances
        const cachedOffers = localStorage.getItem('jobOffersCacheV2')
        const cacheTimestamp = localStorage.getItem('jobOffersCacheV2Timestamp')
        const now = Date.now()
        
        // Utiliser le cache si il a moins de 5 minutes
        if (cachedOffers && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 300000) {
          this.jobOffers = JSON.parse(cachedOffers)
          this.loading = false
          return
        }

        const response = await this.$http.get('/jobs')
        this.jobOffers = response.data.job_offers.data

        // Mettre en cache les données
        localStorage.setItem('jobOffersCacheV2', JSON.stringify(this.jobOffers))
        localStorage.setItem('jobOffersCacheV2Timestamp', now.toString())
      } catch (error) {
        console.error('Erreur lors du chargement des offres:', error)
        if (error.response?.status === 401) {
          this.$router.push('/login')
        }
      } finally {
        this.loading = false
      }
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getRemainingDays(date) {
      const deadline = new Date(date)
      const today = new Date()
      const diffTime = deadline - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return Math.max(0, diffDays)
    },
    
    isExpired(date) {
      return new Date(date) < new Date()
    },
    
    async downloadPdf(offer) {
      if (!offer.pdf_file) return
      
      try {
        const response = await this.$http.get(`/storage/${offer.pdf_file}`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', offer.pdf_file.split('/').pop())
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erreur lors du téléchargement:', error)
        alert('Erreur lors du téléchargement du PDF')
      }
    },
    
    viewDetails(offerId) {
      this.$router.push(`/job-offers/${offerId}`)
    },
    
    applyNow(offerId) {
      if (!localStorage.getItem('token')) {
        this.$router.push('/login')
        return
      }
      this.$router.push(`/jobs/${offerId}/apply`)
    },
    
    shareOnWhatsApp(offer) {
      const text = `Offre d'emploi: ${offer.title} - PREFERE PLUS`
      const url = `${window.location.origin}/jobs/${offer.id}/apply`
      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`)
    },
    
    shareOnFacebook(offer) {
      const url = `${window.location.origin}/jobs/${offer.id}/apply`
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
    },
    
    shareOnLinkedIn(offer) {
      const url = `${window.location.origin}/jobs/${offer.id}/apply`
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)
    },
    
    clearFilters() {
      this.filters.status = ''
      this.filters.search = ''
      this.showAllPast = false
    },

    // Méthode pour rafraîchir le cache
    refreshCache() {
      localStorage.removeItem('jobOffersCacheV2')
      localStorage.removeItem('jobOffersCacheV2Timestamp')
      this.loading = true
      this.loadJobOffers()
    }
  }
}
</script>

<style scoped>
.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  color: #1d9ed9;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.page-subtitle {
  color: #666;
  font-size: 1.1rem;
}

.filters-section {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.job-offers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(350px, 100%), 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.job-offer-item .card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #dee2e6;
}

.job-offer-item .card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.card-title {
  color: #1d9ed9;
  font-size: 1.2rem;
  font-weight: bold;
}

.status-badges {
  display: flex;
  gap: 5px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25em 0.5em;
}

.offer-content {
  color: #666;
  line-height: 1.5;
  max-height: 100px;
  overflow: hidden;
  position: relative;
}

.offer-content::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  width: 40px;
  height: 20px;
  background: linear-gradient(to right, transparent, white);
}

.offer-details {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
}

.detail-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-row i {
  margin-right: 8px;
  width: 16px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.share-buttons {
  display: flex;
  gap: 5px;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.no-offers {
  padding: 60px 20px;
}

/* Sections offres actives / passees */
.offers-section {
  margin-bottom: 20px;
}

.section-header {
  margin-bottom: 10px;
}

.section-subtitle {
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-subtitle i {
  font-size: 1.2rem;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 10px;
  background-color: #1d9ed9;
  color: #fff;
  border-radius: 14px;
  font-size: 0.85rem;
  font-weight: 600;
}

.count-badge.count-muted {
  background-color: #6c757d;
}

.past-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  flex-wrap: wrap;
  padding-top: 20px;
  border-top: 2px dashed #dee2e6;
}

/* Cartes d'offres passees : visuel attenue */
.past-grid .past-offer .card {
  opacity: 0.85;
  background-color: #fafafa;
  border-color: #e5e7eb;
}

.past-grid .past-offer .card:hover {
  opacity: 1;
  transform: translateY(-3px);
}

.past-grid .past-offer .card-title {
  color: #6c757d;
}

.past-grid .past-offer .badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.past-grid .past-offer .offer-content {
  color: #8a8a8a;
}

.past-grid .past-offer .offer-details {
  background-color: #f1f3f5;
}

@media (max-width: 768px) {
  .job-offers-grid {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 1.8rem;
  }

  .action-buttons {
    flex-direction: column;
    gap: 5px;
  }

  .share-buttons {
    justify-content: center;
  }

  .page-header {
    margin-top: 10px;
  }

  .filters-section .row > div {
    margin-bottom: 10px;
  }
}

@media (max-width: 575px) {
  .page-title {
    font-size: 1.5rem;
  }

  .page-subtitle {
    font-size: 0.95rem;
  }

  .section-subtitle {
    font-size: 1.2rem;
  }

  .card-title {
    font-size: 1rem;
  }
}
</style> 